import { usePortalScreen } from '../../app/telemetry/usePortalTelemetry';
import { portalScreenContext, trackPortalEvent } from '../../app/telemetry/portalTelemetry';
import { Box, Button, Grid, IconButton, Paper, Tooltip } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../app/store/configureStore";
import { useReferenceData } from "../../app/customHooks/useReferenceData";
import { ApiRequestStatus } from "../../app/enums/apiRequestStatus";
import LoadingComponent from "../../app/layout/LoadingComponent";
import { MESSAGE_PROCESSING_DETAILS } from "../../app/utils/constant";
import { useEffect, useState } from "react";
import { PcsEventDto } from "../../app/models/events/pcsEventDto";
import React from "react";
import { getDateWithMonthName } from "../../app/utils/util";
import AppPagination from "../../app/components/AppPagination";
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DescriptionIcon from '@mui/icons-material/Description';
import { useHistory } from "react-router-dom";
import { AppPathEnum } from "../../app/enums/appPathEnum";
import EventsHeader from "./common/EventsHeader";
import { getPcsEventsAsync } from "../../app/slices/pcsEventSlice";

export default function EventApprovalForm() {
    const methods = useForm();

    const { staticDataStateStatus } = useReferenceData();
    const { eventStatuses, pcsActivityTypes } = useAppSelector(state => state.ReferenceData);
    const { pcsEvents, getPcsEventsStatus, savePcsEventStatus } = useAppSelector(state => state.pcsEvent);
    const [startIndexForGrid, setStartIndexForGrid] = useState(0);
    const [endIndexForGrid, setEndIndexForGrid] = useState(0);

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(getPcsEventsAsync());
    }, [dispatch]);

    const history = useHistory();

    const cloneEventDetailsAsync = async (eventId: string) => {
        trackPortalEvent(portalScreenContext('EventApprovalForm'), 'ActionSelected', { action: 'clone' });
        history.push(`${AppPathEnum.PCS_EVENT_DETAILS}/${window.location.search}&cloneEventId=${eventId}`);
    }

    usePortalScreen(portalScreenContext('EventApprovalForm'), undefined,
        getPcsEventsStatus !== ApiRequestStatus.Pending && staticDataStateStatus !== ApiRequestStatus.Pending
        && savePcsEventStatus !== ApiRequestStatus.Pending);

    if (getPcsEventsStatus === ApiRequestStatus.Pending || staticDataStateStatus === ApiRequestStatus.Pending
        || savePcsEventStatus === ApiRequestStatus.Pending) {
        return <LoadingComponent message={MESSAGE_PROCESSING_DETAILS} />
    }

    return <FormProvider {...methods}>
        <EventsHeader />

        <Box sx={{ width: '100%' }} component={Paper}>
            <Grid container rowSpacing={3} columnSpacing={1} sx={{ fontSize: 15, pr: '20px', m: '2%' }}>

                <Grid size={{ xs: 12 }}></Grid>

                <Grid size={{ xs: 12 }}>
                    <Button variant="contained" onClick={() => {
                        trackPortalEvent(portalScreenContext('EventApprovalForm'), 'ActionSelected', { action: 'apply' });
                        history.push(`${AppPathEnum.PCS_EVENT_DETAILS}/${window.location.search}`);
                    }}>
                        Apply
                    </Button>
                </Grid>

                <Grid size={{ xs: 2.5, sm: 1.5 }} display={{ xs: 'none', sm: 'block' }}><b>Start Date</b></Grid>

                <Grid size={{ xs: 4, sm: 2 }} display={{ sm: 'block' }}><b>Title</b></Grid>

                <Grid size={{ xs: 1.5 }} display={{ xs: 'none', sm: 'block' }}><b>Type of Activity</b></Grid>

                <Grid size={{ xs: 1.5, sm: 1.5 }} display={{ xs: 'none', sm: 'block' }}><b>Status</b></Grid>

                <Grid size={{ xs: 5, sm: 2 }} display={{ xs: 'block' }}><b>Credits Awarded</b></Grid>

                <Grid size={{ xs: 5, sm: 2 }} display={{ xs: 'none', sm: 'block' }}><b>Created on</b></Grid>

                <Grid size={{ xs: 1, sm: 0.5 }} display={{ xs: 'none', sm: 'block' }}></Grid>

                <Grid size={{ xs: 1, sm: 0.5 }} display={{ xs: 'none', sm: 'block' }}></Grid>

                <Grid size={{ xs: 1, sm: 0.5 }} display={{ xs: 'none', sm: 'block' }}></Grid>

                {pcsEvents?.slice(startIndexForGrid, endIndexForGrid).map((row: PcsEventDto) => (
                    <React.Fragment key={row.eventId}>
                        <Grid size={{ xs: 2.5, sm: 1.5 }} display={{ xs: 'none', sm: 'block' }}>
                            {row.startDate && (new Date(row.startDate).getFullYear() !== 1 ?
                                getDateWithMonthName(new Date(row.startDate)) : '')}
                        </Grid>

                        <Grid size={{ xs: 4, sm: 2 }} display={{ xs: 'block' }}>
                            {row.title}
                        </Grid>

                        <Grid size={{ xs: 1.5 }} display={{ xs: 'none', sm: 'block' }}>
                            {pcsActivityTypes?.find(x => x.id === row.activityTypeId)?.value}
                        </Grid>

                        <Grid size={{ xs: 1.5, sm: 1.5 }} display={{ xs: 'none', sm: 'block' }}>
                            {eventStatuses?.find(x => x.id === row.eventStatusId)?.value}
                        </Grid>

                        <Grid size={{ xs: 5, sm: 2 }} display={{ xs: 'block' }}>
                            {row.totalCpdCredits && parseFloat(row.totalCpdCredits).toFixed(2)}
                            {!row.totalCpdCredits && '-'}
                        </Grid>

                        <Grid size={{ xs: 5, sm: 2 }} display={{ xs: 'none', sm: 'block' }}>
                            {row.modifiedOn && getDateWithMonthName(new Date(row.modifiedOn))}
                        </Grid>

                        {eventStatuses?.find(x => x.id === row.eventStatusId)?.userEditable === true &&
                            <Grid size={{ xs: 1, sm: 0.5 }} display={{ xs: 'block' }} sx={{ textAlign: 'center' }}>
                                <Tooltip title="Update record">
                                    <IconButton
                                        sx={{
                                            color: 'primary.main', borderRadius: 2,
                                            '&:hover': {
                                                backgroundColor: 'primary.main', color: 'white'
                                            },
                                        }} onClick={() => {
                        trackPortalEvent(portalScreenContext('EventApprovalForm'), 'ActionSelected', { action: 'edit' });
                        history.push(`${AppPathEnum.PCS_EVENT_DETAILS}/${window.location.search}&eventId=${row.eventId}`);
                    }}>
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                            </Grid>}

                        {eventStatuses?.find(x => x.id === row.eventStatusId)?.userEditable === false &&
                            <Grid size={{ xs: 1, sm: 0.5 }} display={{ xs: 'block' }} sx={{ textAlign: 'center' }}>
                                <Tooltip title="View record">
                                    <IconButton
                                        sx={{
                                            color: 'primary.main', borderRadius: 2,
                                            '&:hover': {
                                                backgroundColor: 'primary.main', color: 'white'
                                            },
                                        }} onClick={() => {
                        trackPortalEvent(portalScreenContext('EventApprovalForm'), 'ActionSelected', { action: 'view' });
                        history.push(`${AppPathEnum.PCS_EVENT_DETAILS}/${window.location.search}&eventId=${row.eventId}`);
                    }}>
                                        <DescriptionIcon />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        }

                        <Grid size={{ xs: 1, sm: 0.5 }} display={{ xs: 'block' }} sx={{ textAlign: 'center' }}>
                            <Tooltip title="Clone record">
                                <IconButton
                                    sx={{
                                        color: 'primary.main', borderRadius: 2,
                                        '&:hover': {
                                            backgroundColor: 'primary.main', color: 'white'
                                        },
                                    }} onClick={() => row.eventId && cloneEventDetailsAsync(row.eventId)}>
                                    <ContentCopyIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </React.Fragment>
                ))}

                {pcsEvents?.length === 0 &&
                    <Grid size={{ xs: 12 }} sx={{ textAlign: 'center' }}>
                        No events found.
                    </Grid>
                }

                <Grid size={{ xs: 12 }}></Grid>
                <Grid size={{ xs: 12 }}></Grid>

                <AppPagination startIndex={(data: number) => setStartIndexForGrid(data)}
                    endIndex={(data: number) => setEndIndexForGrid(data)} recordsCount={pcsEvents?.length ?? 0} />
            </Grid>
        </Box>
    </FormProvider>
}
